import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RANGES = [{ d: 1, l: '24h' }, { d: 7, l: '7d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }];
const TIERS = ['MINI', 'MINOR', 'MAJOR', 'MEGA'];
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;

const Kpi = ({ label, value, sub, color, testid }) => (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">{label}</div>
        <div className="font-mono font-black text-xl mt-1" style={{ color: color || '#fff' }}>{value}</div>
        {sub != null && <div className="text-[10px] text-[#A0AAB2] mt-0.5">{sub}</div>}
    </div>
);

export const OutlawsLootReportsAdminTab = () => {
    const [days, setDays] = useState(30);
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/outlaws-loot/admin/report?days=${d}`, { withCredentials: true });
            setRep(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load Outlaw's Loot report");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(days); }, [days, load]);

    const cert = rep?.certified_rtp_pct ?? 87.76;
    const rtpDelta = rep ? rep.rtp_pct - cert : 0;
    const rtpColor = !rep || rep.spins === 0 ? '#A0AAB2' : (Math.abs(rtpDelta) <= 6 ? '#37d67a' : (rtpDelta < 0 ? '#ffb020' : '#ff6b6b'));
    const ggrPos = rep && rep.ggr >= 0;

    return (
        <div className="card-dark p-5 space-y-4" data-testid="outlaws-loot-reports-admin">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#e8b64c]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Outlaw's Loot — Reports</h3>
                </div>
                <div className="flex items-center gap-1">
                    {RANGES.map((r) => (
                        <Button key={r.d} size="sm" onClick={() => setDays(r.d)}
                            variant={days === r.d ? 'default' : 'ghost'}
                            className={`h-8 text-xs ${days === r.d ? 'bg-[#e8b64c] text-black hover:bg-[#e8b64c]/90' : 'text-[#A0AAB2]'}`}
                            data-testid={`ol-report-range-${r.d}`}>{r.l}</Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => load(days)} className="text-[#A0AAB2] h-8 text-xs" data-testid="ol-report-refresh">
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>
            <p className="text-[11px] text-[#A0AAB2]">
                Live financials from settled spins over the window. Handle = total wagered · GGR = house gross win (handle − paid) ·
                RTP is compared against the certified <b className="text-[#e8b64c]">{pct(cert)}</b>. Short windows swing widely
                (jackpots are rare); RTP converges to certified only over large volume.
            </p>

            {!rep ? (
                <div className="text-center text-[#A0AAB2] py-10 text-sm" data-testid="ol-report-loading">Loading…</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Spins" value={Number(rep.spins || 0).toLocaleString()} testid="ol-kpi-spins" />
                        <Kpi label="Wagered (handle)" value={usd(rep.wagered)} testid="ol-kpi-wagered" />
                        <Kpi label="Paid to players" value={usd(rep.paid)} sub={`regular ${usd(rep.regular_paid)} · jackpot ${usd(rep.jackpot_paid)}`} testid="ol-kpi-paid" />
                        <Kpi label="GGR (house win)" value={usd(rep.ggr)} color={ggrPos ? '#37d67a' : '#ff6b6b'} testid="ol-kpi-ggr" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Actual RTP" value={pct(rep.rtp_pct)} color={rtpColor}
                            sub={<span className="inline-flex items-center gap-1">{rtpDelta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{`${rtpDelta >= 0 ? '+' : ''}${rtpDelta.toFixed(2)} vs cert`}</span>}
                            testid="ol-kpi-rtp" />
                        <Kpi label="Certified RTP" value={pct(cert)} color="#e8b64c" testid="ol-kpi-cert-rtp" />
                        <Kpi label="Hold %" value={pct(rep.hold_pct)} testid="ol-kpi-hold" />
                        <Kpi label="Feature (bonus) rate" value={rep.feature_one_in ? `1 in ${rep.feature_one_in}` : '—'} sub={`${pct(rep.feature_rate_pct)} · ${rep.features} hits`} testid="ol-kpi-feature" />
                    </div>

                    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="ol-jackpot-hits">
                        <div className="text-[11px] uppercase tracking-wide text-[#A0AAB2] mb-3">Jackpot hits (settled)</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {TIERS.map((tier) => {
                                const h = rep.jackpot_hits?.[tier] || { count: 0, amount: 0 };
                                return (
                                    <div key={tier} className="p-3 rounded-lg bg-black/30 border border-white/5 text-center" data-testid={`ol-jp-${tier}`}>
                                        <div className="text-[10px] uppercase tracking-wide text-[#e8b64c] font-bold">{tier}</div>
                                        <div className="font-mono font-black text-lg text-white mt-1" data-testid={`ol-jp-${tier}-count`}>{h.count}</div>
                                        <div className="text-[10px] text-[#A0AAB2]">{usd(h.amount)} paid</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="text-[10px] text-[#A0AAB2]">Window: last {rep.days} day(s) · mode: {rep.mode}</div>
                </>
            )}
        </div>
    );
};

export default OutlawsLootReportsAdminTab;
